import { LoadingSkeleton } from "./LoadingSkeleton";
import { FieldRequirement } from "./FieldRequirement";
import { useEffect, useRef, useState } from "react";
import type { Organization } from "../domain";
import { request } from "../api/client";
import { useLocale } from "../i18n";
import { AppSelect } from "./inputs/AppSelect";
import { BuildingOffice2Icon } from "@heroicons/react/24/outline";

type Page = { items: Organization[]; nextCursor: string | null };
export function OrganizationSwitcher({ current, onChange, busy }: {
  current: Organization; onChange: (id: string) => Promise<void>; busy: boolean;
}) {
  const { w } = useLocale();
  const [items, setItems] = useState<Organization[]>([current]), [search, setSearch] = useState("");
  const [cursor, setCursor] = useState<string | null>(null), [loading, setLoading] = useState(false);
  const [error, setError] = useState(false), [retry, setRetry] = useState(0);
  const active = useRef<AbortController | null>(null), pending = useRef(false);
  useEffect(() => {
    const controller = new AbortController(); active.current = controller; pending.current = true;
    setLoading(true); setError(false); setCursor(null); setItems([current]);
    const timer = setTimeout(() => {
      request<Page>("/organizations?" + new URLSearchParams({ search }), "GET", undefined, controller.signal)
        .then(page => { if (!controller.signal.aborted) { setItems([current, ...page.items.filter(org => org.id !== current.id)]); setCursor(page.nextCursor); } })
        .catch(() => { if (!controller.signal.aborted) setError(true); })
        .finally(() => { if (!controller.signal.aborted) { pending.current = false; setLoading(false); } });
    }, search ? 250 : 0);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [current.id, current.name, search, retry]);
  async function more() {
    const controller = active.current;
    if (!cursor || pending.current || busy || !controller || controller.signal.aborted) return;
    pending.current = true; setLoading(true); setError(false);
    try {
      const page = await request<Page>("/organizations?" + new URLSearchParams({ cursor, search }), "GET", undefined, controller.signal);
      if (controller.signal.aborted) return;
      if (page.nextCursor === cursor) throw new Error("Invalid organization page");
      setItems(previous => [...previous, ...page.items.filter(org => !previous.some(old => old.id === org.id))]); setCursor(page.nextCursor);
    } catch { if (!controller.signal.aborted) setError(true); }
    finally { if (!controller.signal.aborted) { pending.current = false; setLoading(false); } }
  }
  return <section className="organization-switcher" aria-busy={busy || loading}>
    <label htmlFor="current-organization"><span className="field-caption"><BuildingOffice2Icon aria-hidden="true" />{w("المنشأة الحالية", "Current organization")}<FieldRequirement required /></span></label>
    <AppSelect id="current-organization" title={current.name} value={current.id} required inlineMenu disabled={busy} loading={loading}
      onSearch={setSearch} onMenuScrollToBottom={() => void more()}
      onChange={id => { setError(false); void onChange(id).then(() => setSearch("")).catch(() => setError(true)); }}>
      {items.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
    </AppSelect>
    {busy && <LoadingSkeleton variant="inline" label={w("جارٍ تبديل المنشأة…", "Switching organization…")} />}
    {cursor && <button className="text-link" type="button" disabled={busy || loading} onClick={() => void more()}>{w("عرض المزيد", "Load more")}</button>}
    {error && <div role="alert"><p>{w("تعذر تحديث المنشأة. حاول مجدداً.", "Could not update organization. Please retry.")}</p><button className="text-link" type="button" onClick={() => setRetry(value => value + 1)}>{w("تحديث القائمة", "Refresh list")}</button></div>}
  </section>;
}
